import { Controller } from "@hotwired/stimulus";

// Service combobox for the schedule quick-create modal.
// Server pre-renders ALL active services into a hidden source list, each
// tagged with `data-assigned-staff-ids`. JS filters per the currently chosen
// staff and surfaces an inline "Assign to <staff>" button for unassigned
// services. Clicking Assign POSTs to /operations/staffs/:staff_id/assign_service
// then auto-selects the service.
//
// Markup contract — see _quick_form_modal.html.erb:
//   data-controller="quick-service-combobox"
//   data-quick-service-combobox-staff-id-value="<staff id>"
//   data-quick-service-combobox-staff-name-value="<staff display name>"
//   data-quick-service-combobox-assign-url-template-value="/operations/staffs/:id/assign_service"
//   data-quick-service-combobox-allow-create-value="true"  (optional)
//   <input data-quick-service-combobox-target="input">
//   <input data-quick-service-combobox-target="hiddenId" hidden>
//   <input data-quick-service-combobox-target="newName" hidden>  (optional)
//   <ul data-quick-service-combobox-target="results"></ul>
//   <template data-quick-service-combobox-target="source">
//     <li data-service-id="..." data-name="..." data-assigned-staff-ids="1,2,3"></li>
//     ...
//   </template>
//
// Listen on the form for staff change so we can re-tag entries:
//   form: data-action="change->quick-service-combobox#staffChanged"
export default class extends Controller {
  static targets = ["input", "results", "hiddenId", "newName", "source"];
  static values = {
    staffId: String,
    staffName: String,
    assignUrlTemplate: String,
    allowCreate: { type: Boolean, default: false },
  };

  connect() {
    this._sourceItems = Array.from(this.sourceTarget.content.querySelectorAll("[data-service-id]"))
      .map((node) => ({
        id: node.dataset.serviceId,
        name: node.dataset.name,
        assignedStaffIds: (node.dataset.assignedStaffIds || "").split(",").filter(Boolean),
      }));

    this._onDocumentClick = (e) => {
      if (!this.element.contains(e.target)) this._hide();
    };
    document.addEventListener("click", this._onDocumentClick);
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick);
  }

  // Wired from the form-level listeners — picks up staff changes from either
  // the Day-view staff <select> (native change) or the Week-view staff
  // combobox (staff-combobox:changed CustomEvent).
  staffChanged(event) {
    if (event.type === "staff-combobox:changed") {
      this.staffIdValue = event.detail?.id || "";
      this.staffNameValue = event.detail?.name || "";
    } else {
      const target = event.target;
      if (!target?.name?.endsWith("[staff_id]")) return;
      this.staffIdValue = target.value || "";
      this.staffNameValue = target.selectedOptions?.[0]?.textContent?.trim() || "";
    }
    if (this.inputTarget.value.trim()) this._renderFiltered(this.inputTarget.value.trim());
  }

  onInput() {
    const q = this.inputTarget.value.trim();
    this._syncHidden(q);
    this._renderFiltered(q);
  }

  onFocus() {
    this._renderFiltered(this.inputTarget.value.trim());
  }

  _renderFiltered(q) {
    const ql = q.toLowerCase();
    const items = this._sourceItems.filter((s) => !ql || s.name.toLowerCase().includes(ql));
    const exact = this._sourceItems.some((s) => s.name.toLowerCase() === ql);
    const canCreate = this.allowCreateValue && q.length > 0 && !exact;

    this.resultsTarget.innerHTML = "";
    if (items.length === 0 && !canCreate) {
      this._hide();
      return;
    }

    const assigned = items.filter((s) => this._isAssigned(s));
    const unassigned = items.filter((s) => !this._isAssigned(s));

    assigned.forEach((s) => this.resultsTarget.appendChild(this._buildAssignedItem(s)));

    if (unassigned.length > 0) {
      const header = document.createElement("li");
      header.className = "px-3 py-1 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50 border-t border-gray-200";
      header.textContent = this.staffIdValue ? `Not assigned to ${this.staffNameValue || "staff"}` : "All services";
      this.resultsTarget.appendChild(header);
      unassigned.forEach((s) => this.resultsTarget.appendChild(this._buildUnassignedItem(s)));
    }

    if (canCreate) {
      if (items.length > 0) {
        const sep = document.createElement("li");
        sep.className = "border-t border-gray-200";
        sep.setAttribute("role", "separator");
        this.resultsTarget.appendChild(sep);
      }
      this.resultsTarget.appendChild(this._buildCreateItem(q));
    }

    this._show();
  }

  _buildCreateItem(name) {
    const li = document.createElement("li");
    li.className = "px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm";
    const label = document.createElement("span");
    label.className = "text-gray-500";
    label.textContent = "+ Create ";
    const value = document.createElement("span");
    value.className = "font-medium text-black";
    value.textContent = `"${name}"`;
    li.appendChild(label);
    li.appendChild(value);
    li.addEventListener("click", () => this._pickCreate(name));
    return li;
  }

  _pickCreate(name) {
    this.hiddenIdTarget.value = "";
    if (this.hasNewNameTarget) this.newNameTarget.value = name;
    this.inputTarget.value = name;
    this._hide();
  }

  // Keep hidden fields consistent when the operator types but submits without
  // clicking a row: exact match -> service_id, otherwise -> new_service_name.
  _syncHidden(q) {
    const exact = this._sourceItems.find((s) => s.name.toLowerCase() === q.toLowerCase());
    if (exact) {
      this.hiddenIdTarget.value = exact.id;
      if (this.hasNewNameTarget) this.newNameTarget.value = "";
    } else {
      this.hiddenIdTarget.value = "";
      if (this.hasNewNameTarget) this.newNameTarget.value = this.allowCreateValue ? q : "";
    }
  }

  _isAssigned(service) {
    if (!this.staffIdValue) return false;
    return service.assignedStaffIds.includes(String(this.staffIdValue));
  }

  _buildAssignedItem(service) {
    const li = document.createElement("li");
    li.className = "px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm font-medium text-black";
    li.textContent = service.name;
    li.addEventListener("click", () => this._pick(service));
    return li;
  }

  _buildUnassignedItem(service) {
    // No concrete staff id (nothing chosen yet, or a brand-new inline-created
    // staff the backend will auto-link the service to): there is nothing to
    // "assign" against, so let the operator pick the service directly.
    if (!this.staffIdValue) {
      const li = document.createElement("li");
      li.className = "px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm font-medium text-black";
      li.textContent = service.name;
      li.addEventListener("click", () => this._pick(service));
      return li;
    }

    const li = document.createElement("li");
    li.className = "px-3 py-2 text-sm flex items-center justify-between gap-2 border-t border-gray-100";
    const left = document.createElement("span");
    left.className = "text-gray-700";
    left.textContent = service.name;
    li.appendChild(left);

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "text-xs font-bold text-black border border-gray-300 rounded px-2 py-1 hover:bg-gray-100";
    btn.textContent = `Assign to ${this.staffNameValue || "staff"}`;
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      this._assign(service, btn);
    });
    li.appendChild(btn);
    return li;
  }

  _pick(service) {
    this.hiddenIdTarget.value = service.id;
    if (this.hasNewNameTarget) this.newNameTarget.value = "";
    this.inputTarget.value = service.name;
    this._hide();
  }

  async _assign(service, btn) {
    if (!this.staffIdValue) return;
    btn.disabled = true;
    btn.textContent = "Assigning…";

    const url = this.assignUrlTemplateValue.replace(":id", encodeURIComponent(this.staffIdValue));
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrf(),
        },
        body: JSON.stringify({ service_id: service.id }),
      });
      if (!res.ok) throw new Error();
      // Mark locally as assigned so re-renders surface it under the assigned section.
      service.assignedStaffIds.push(String(this.staffIdValue));
      this._pick(service);
    } catch (_) {
      btn.disabled = false;
      btn.textContent = "Failed — retry";
    }
  }

  _csrf() {
    return document.querySelector("meta[name=csrf-token]")?.content || "";
  }

  _show() { this.resultsTarget.classList.remove("hidden"); }
  _hide() { this.resultsTarget.classList.add("hidden"); }
}
